<template>
  <div class="footer">
    <div class="footer-content">
      <p class="copyright">&copy; 2026 Renants. All rights reserved.</p>
      <div class="footer-links">
        <router-link to="/about" class="footer-link">About</router-link>
        <span class="separator">|</span>
        <router-link to="/why-renants" class="footer-link">Why Renants</router-link>
        <span class="separator">|</span>
        <router-link to="/contact-us" class="footer-link">Contact</router-link>
        <span class="separator">|</span>
        <router-link to="/terms-and-conditions" class="footer-link">Terms & Conditions</router-link>
        <span class="separator">|</span>
        <router-link to="/privacy-policy" class="footer-link">Privacy Policy</router-link>
      </div>
      <div class="version-info" v-if="buildLabel">
        <span
          class="version-text"
          :title="buildTitle"
          @click="copySha"
          role="button"
          tabindex="0"
          @keydown.enter.prevent="copySha"
          @keydown.space.prevent="copySha"
        >{{ copied ? 'copied!' : buildLabel }}</span>
      </div>
    </div>
  </div>
</template>
<script>
// __APP_VERSION__ is baked in at build time — see vue-app/vite.config.ts
// and the type declaration in env.d.ts.
export default {
  name: "FooterSection",
  data() {
    // eslint-disable-next-line no-undef
    const bundle = (typeof __APP_VERSION__ !== "undefined")
      // eslint-disable-next-line no-undef
      ? __APP_VERSION__
      : { commit: "", branch: "", builtAt: "", lastPulledAt: "" };
    return {
      bundle,
      serverVersion: null,
      serverError: false,
      copied: false,
    };
  },
  computed: {
    buildLabel() {
      if (!this.bundle.commit) return "";
      const parts = [`${this.bundle.branch}@${this.bundle.commit}`];
      if (this.lastPulledShort) parts.push(this.lastPulledShort);
      return parts.join(" · ");
    },
    lastPulledShort() {
      // mm/dd of when the working copy last synced with origin
      if (!this.bundle.lastPulledAt) return "";
      const d = new Date(this.bundle.lastPulledAt);
      if (isNaN(d.getTime())) return "";
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${mm}/${dd}`;
    },
    inSync() {
      if (this.serverError || !this.serverVersion) return null;
      return this.serverVersion.commit === this.bundle.commit;
    },
    buildTitle() {
      const lines = [
        `commit:      ${this.bundle.commit}`,
        `branch:      ${this.bundle.branch}`,
        `last pulled: ${this.bundle.lastPulledAt}`,
        `built:       ${this.bundle.builtAt}`,
      ];
      if (this.serverError) lines.push("server:      unreachable");
      else if (this.serverVersion) {
        lines.push(
          this.inSync
            ? "server:      in sync"
            : `server:      mismatch (${this.serverVersion.commit})`
        );
      }
      lines.push("", "(click to copy SHA)");
      return lines.join("\n");
    },
  },
  mounted() {
    fetch("/api/version", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((v) => { this.serverVersion = v; })
      .catch(() => { this.serverError = true; });
  },
  methods: {
    async copySha() {
      if (!this.bundle.commit) return;
      try {
        await navigator.clipboard.writeText(this.bundle.commit);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 1500);
      } catch {
        // clipboard blocked — no-op
      }
    },
  },
};
</script>
<style scoped>
.footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 32px;
  gap: 8px;
  width: 100%;
  background: #f8f9fb;
  border-top: 1px solid #e5e7eb;
  box-sizing: border-box;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 32px;
  width: 100%;
  max-width: 1440px;
}

.copyright {
  margin: 0;
  font-size: 14px;
  color: #666666;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
}

.footer-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 0;
  flex-wrap: nowrap;
}

.footer-link {
  color: #3467d5;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  white-space: nowrap;
}

.footer-link:hover {
  color: #2651b1;
  text-decoration: underline;
}


.version-info {
  display: flex;
  align-items: center;
}

.version-text {
  font-size: 11px;
  color: #999999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-weight: 400;
  cursor: pointer;
  transition: color 0.15s ease;
}
.version-text:hover,
.version-text:focus {
  color: #666666;
  outline: none;
}
.separator {
  color: #cccccc;
  font-size: 13px;
  line-height: 1;
}

/* Tablet devices */
@media (max-width: 768px) {
  .footer {
    padding: 24px;
    gap: 12px;
  }

  .footer-content {
    padding: 0 24px;
    gap: 12px;
  }

  .version-text {
    font-size: 10px;
  }

  .copyright {
    font-size: 13px;
  }

  .footer-link {
    font-size: 12px;
  }

  .separator {
    font-size: 12px;
  }
}

/* Mobile devices */
@media (max-width: 480px) {
  .footer {
    padding: 20px 16px;
    gap: 12px;
    box-sizing: border-box;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .footer-content {
    flex-direction: column;
    padding: 0;
    gap: 12px;
    align-items: center;
    box-sizing: border-box;
    max-width: 100%;
  }

  .copyright {
    font-size: 12px;
    text-align: center;
  }

  .footer-links {
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
  }

  .footer-link {
    font-size: 11px;
  }

  .version-text {
    font-size: 9px;
  }

  .separator {
    font-size: 11px;
  }
}
</style>
